// src/store/employeeSlice.ts
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

/** 所属情報（複数所属対応） */
export interface Affiliation {
  organizationId: string;
  organizationName: string;
  positionId: string;
  positionName: string;
  order: number | null;
  /** 親組織ID */
  parentOrganizationId?: string;
  /** 子孫組織の組織分類IDリスト */
  descendantOrganizationClassIds?: string[];
}

interface EmployeeState {
  employeeId: string;
  employeeName: string;
  /** 主所属の事業部ID（affiliations[0]から取得、後方互換用） */
  divisionId: string;
  /** 主所属の事業部名（affiliations[0]から取得、後方互換用） */
  divisionName: string;
  /** 主所属の親組織ID（affiliations[0]から取得） */
  parentOrganizationId: string;
  /** 主所属の子孫組織分類IDリスト（affiliations[0]から取得） */
  descendantOrganizationClassIds: string[];
  /** 複数所属情報（配列） */
  affiliations: Affiliation[];
  /** ユーザー権限（1:一般権限, 2:事業閲覧権限, 3:全社閲覧権限） */
  userRole: string | null;
  /** マスタメンテナンス表示可否 */
  isMasterMaintenanceAccessible: boolean;
  /** 本社経営管理 */
  isHeadquartersManagement: boolean;
  /** 生成AI利用制限フラグ（所属組織がブラックリスト登録済みの場合 true） */
  isAIRestricted: boolean;
  /** システム全体のAI利用可否フラグ（GCP Gemini利用グループへの所属で判定） */
  isAIEnabled: boolean;
}

const initialState: EmployeeState = {
  employeeId: '',
  employeeName: '',
  divisionId: '',
  divisionName: '',
  parentOrganizationId: '',
  descendantOrganizationClassIds: [],
  affiliations: [],
  userRole: null,
  isMasterMaintenanceAccessible: false,
  isHeadquartersManagement: false,
  isAIRestricted: false,
  isAIEnabled: false,
};

const employeeSlice = createSlice({
  name: "employee",
  initialState,
  reducers: {
    setEmployeeInfo: (state, action: PayloadAction<Omit<EmployeeState, 'isAIRestricted'>>) => {
      state.employeeId = action.payload.employeeId; // 社員ID
      state.employeeName = action.payload.employeeName; // 社員名
      state.divisionId = action.payload.divisionId; // 事業部ID（affiliations[0]から）
      state.divisionName = action.payload.divisionName; // 事業部名（affiliations[0]から）
      state.parentOrganizationId = action.payload.parentOrganizationId; // 親組織ID（affiliations[0]から）
      state.descendantOrganizationClassIds = action.payload.descendantOrganizationClassIds; // 子孫組織分類IDリスト（affiliations[0]から）
      state.affiliations = action.payload.affiliations; // 複数所属情報
      state.userRole = action.payload.userRole; // ユーザー権限
      state.isMasterMaintenanceAccessible = action.payload.isMasterMaintenanceAccessible; // マスタメンテナンス表示可否
      state.isHeadquartersManagement = action.payload.isHeadquartersManagement; // 本社経営管理
      // isAIRestricted はプロジェクト単位で setIsAIRestricted が管理するため、ここでは更新しない
      state.isAIEnabled = action.payload.isAIEnabled ?? false; // システム全体AI利用可否
    },
    setIsAIRestricted: (state, action: PayloadAction<boolean>) => {
      state.isAIRestricted = action.payload;
    },
    setIsAIEnabled: (state, action: PayloadAction<boolean>) => {
      state.isAIEnabled = action.payload;
    },
  },
});

export const { setEmployeeInfo, setIsAIRestricted, setIsAIEnabled } = employeeSlice.actions;
export default employeeSlice.reducer;
